<script setup lang="ts">
import { __ } from '@@/bits/intl'
import type { UserFieldValue } from '@@/types'
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { processPostBody } from '@@/vuecomposables/process_post_or_comment_body'
import { computed, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

function assertIsUserFieldValue(value: unknown): asserts value is UserFieldValue {
  if (
    typeof value !== 'object' ||
    value == null ||
    !Object.keys(value).every((key) => key === 'username' || key === 'name' || key === 'id')
  ) {
    throw new Error('Expected UserFieldValue but got: ' + typeof value)
  }
}

const getProfileUrl = (value: UserFieldValue) => {
  return `${window.location.origin}/${value.username}`
}

const userFieldValue = computed(() => {
  assertIsUserFieldValue(props.postProperty.formattedValue)
  return props.postProperty.formattedValue
})

const userFieldHtml = ref<string | null>(null)
const userFieldHtmlRef = ref<HTMLElement | null>(null)

const replaceTextContent = (elementString: string) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(elementString, 'text/html')
  const aTag = doc.querySelector('a')
  if (!aTag) return null
  aTag.textContent = getNameFromUserFieldValue(userFieldValue.value)
  return aTag.outerHTML
}

const getNameFromUserFieldValue = (value: UserFieldValue) => {
  if (value.name !== '' && value.name != null) return value.name
  if (value.username !== '' && value.username != null) return value.username
  return __('User not found')
}

const processUserFieldValue = async (value: string): Promise<void> => {
  if (!userFieldHtmlRef.value) return
  const string = await processPostBody(value, userFieldHtmlRef.value)
  const newString = replaceTextContent(string)
  if (!newString) return
  userFieldHtml.value = newString
}

onMounted(async () => {
  await processUserFieldValue(getProfileUrl(userFieldValue.value))
})

watch(userFieldValue, async (newValue) => {
  await processUserFieldValue(getProfileUrl(newValue))
})
</script>

<template>
  <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
  <component
    :is="as"
    ref="userFieldHtmlRef"
    :class="[
      'user-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
    v-html="userFieldHtml"
  ></component>
</template>
<style>
.user-field-value a {
  color: currentColor;
  text-decoration-line: underline;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
</style>
